import { supabase } from "./supabase";
import type { Oekt } from "./session";

/**
 * Rapportene, slik redaktøren leser dem.
 *
 * ÉN BOKS PER REGI, som loggflata. Rapporten er regis dokument om dagen
 * sin, og den som leter etter en leter etter *hvem som skrev den* — ikke
 * etter en dato i en felles liste.
 *
 * ALLE, IKKE BARE DAGENS. Dagvelgeren styrer ikke denne flata: en rapport
 * leses oftest i ettertid, og «hva sto det på dag 6?» er hele poenget med å
 * ta vare på dem. Nyeste øverst.
 *
 * FLERE VERSJONER AV SAMME DAG ER FLERE RAPPORTER. Regi kan rette og sende
 * på nytt, og da øker versjonen. Begge blir stående: den som har lest v1
 * skal kunne se at det kom en v2, og hva som var der før.
 *
 * Selve PDF-en ligger i bøtta `rapporter`. Den er privat — lenka hentes
 * gjennom en policy, ikke gjennom en gjettbar URL.
 */

export const BOETTE = "rapporter";

export interface Rapport {
  readonly id: string;
  readonly versjon: number;
  readonly opptaksdag: string;
  /** Datoen regi førte opp ved avslutning, ISO. Tom når den mangler. */
  readonly dato: string;
  readonly lag: string;
  readonly skift: string;
  readonly oppsummering: string;
  readonly filsti: string | null;
  /** Når dagen ble avsluttet, og når rapporten faktisk kom fram. */
  readonly lukket: string | null;
  readonly levert: string | null;
}

export interface Rapportkasse {
  /** Medlemmet. Boksen ER personen. */
  readonly id: string;
  readonly forfatter: string;
  readonly initialer: string;
  readonly rapporter: readonly Rapport[];
}

interface Rad { readonly [k: string]: unknown }

const str = (v: unknown): string => String(v ?? "");

/** Oppsummeringen ligger som blokker. I v1 finnes bare `paragraph`. */
function oppsummeringAv(blocks: unknown): string {
  if (!Array.isArray(blocks)) return "";
  return blocks
    .map((b) => (b as Rad)?.["text"])
    .filter((t): t is string => typeof t === "string")
    .join("\n\n");
}

export async function hentRapporter(oekt: Oekt): Promise<Rapportkasse[]> {
  if (supabase === null) return [];

  const { data, error } = await supabase
    .from("report")
    .select(
      "id, version, status, blocks, meta, pdf_path, closed_at, delivered_at, author_id,"
      + " production_member!report_author_id_fkey(display_name, initials),"
      + " log_session(shoot_day)",
    )
    .eq("production_id", oekt.production.id)
    .order("created_at", { ascending: false });

  if (error !== null || data === null) return [];

  const kasser = new Map<string, {
    forfatter: string; initialer: string; rapporter: Rapport[];
  }>();

  for (const rad of data as unknown as Rad[]) {
    const medlem = (rad["production_member"] ?? {}) as Rad;
    const oekta = (rad["log_session"] ?? {}) as Rad;
    const meta = (rad["meta"] ?? {}) as Rad;
    const id = str(rad["author_id"]);

    let kasse = kasser.get(id);
    if (kasse === undefined) {
      kasse = {
        forfatter: str(medlem["display_name"]),
        initialer: str(medlem["initials"]),
        rapporter: [],
      };
      kasser.set(id, kasse);
    }

    kasse.rapporter.push({
      id: str(rad["id"]),
      versjon: Number(rad["version"] ?? 1),
      /* Opptaksdagen tas fra ØKTEN og ikke fra `meta`: meta er det regi
         skrev inn i skjemaet, økten er hva loggen faktisk hørte til. */
      opptaksdag: str(oekta["shoot_day"]),
      dato: str(meta["dateIso"]),
      lag: str(meta["unit"]),
      skift: str(meta["shift"]),
      oppsummering: oppsummeringAv(rad["blocks"]),
      filsti: rad["pdf_path"] === null || rad["pdf_path"] === undefined
        ? null : str(rad["pdf_path"]),
      lukket: rad["closed_at"] === null || rad["closed_at"] === undefined
        ? null : str(rad["closed_at"]),
      levert: rad["delivered_at"] === null || rad["delivered_at"] === undefined
        ? null : str(rad["delivered_at"]),
    });
  }

  return [...kasser].map(([id, k]) => ({ id, ...k }));
}

/**
 * En lenke til PDF-en som varer i en time.
 *
 * Bøtta er privat. Lenka lages ved trykket og ikke ved tegningen: en liste
 * med tjue rapporter skulle ellers hentet tjue signaturer for å vise noe
 * ingen har bedt om ennå.
 */
export async function lenke(filsti: string): Promise<string | null> {
  if (supabase === null) return null;
  const { data, error } = await supabase.storage
    .from(BOETTE)
    .createSignedUrl(filsti, 3600);
  if (error !== null || data === null) return null;
  return data.signedUrl;
}
